import React from "react";
import { formatUnits } from "viem";
import { GitBranch } from "lucide-react";

export default function RoutePanel({ ctx }) {
  const q = ctx?.raw || ctx?.quote;
  const fromToken = ctx?.fromToken;
  const toToken = ctx?.toToken;
  const fromChain = ctx?.fromChain;
  const toChain = ctx?.toChain;

  if (!q) {
    return (
      <div className="px-4 py-8 sm:py-10 text-center">
        <div className="mx-auto w-10 h-10 rounded-lg bg-[var(--panel-2)] border border-[var(--border)] flex items-center justify-center mb-3">
          <GitBranch size={16} className="text-[var(--text-muted)]" />
        </div>
        <div className="text-[13px] text-[var(--text)] font-medium">Route preview</div>
        <div className="text-[12px] text-[var(--text-muted)] mt-1 max-w-[240px] mx-auto">
          Enter an amount to see the best cross-chain path across 45+ DEXs and 15+ bridges.
        </div>
      </div>
    );
  }

  const est = q.estimate || {};
  const steps = q.includedSteps || q.steps || [q];

  return (
    <div className="px-3 sm:px-4 py-4">
      <div className="grid grid-cols-2 gap-2 mb-4">
        <div className="panel-3 p-3">
          <div className="text-[10.5px] text-[var(--text-subtle)] uppercase tracking-wider">Min received</div>
          <div className="num-md text-[var(--text)] mt-1 truncate">
            {est.toAmountMin && toToken ? Number(formatUnits(BigInt(est.toAmountMin), toToken.decimals)).toFixed(6) : "—"}
          </div>
          <div className="text-[11px] text-[var(--text-muted)]">{toToken?.symbol}</div>
        </div>
        <div className="panel-3 p-3">
          <div className="text-[10.5px] text-[var(--text-subtle)] uppercase tracking-wider">Est. time</div>
          <div className="num-md text-[var(--text)] mt-1">{est.executionDuration ? `${est.executionDuration}s` : "—"}</div>
          <div className="text-[11px] text-[var(--text-muted)]">to finality</div>
        </div>
        <div className="panel-3 p-3">
          <div className="text-[10.5px] text-[var(--text-subtle)] uppercase tracking-wider">Gas (est.)</div>
          <div className="num-md text-[var(--text)] mt-1">
            {est.gasCosts?.[0]?.amountUSD ? `$${parseFloat(est.gasCosts[0].amountUSD).toFixed(2)}` : "—"}
          </div>
          <div className="text-[11px] text-[var(--text-muted)]">source chain</div>
        </div>
        <div className="panel-3 p-3">
          <div className="text-[10.5px] text-[var(--text-subtle)] uppercase tracking-wider">Integrator fee</div>
          <div className="num-md text-[var(--accent)] mt-1">0.50%</div>
          <div className="text-[11px] text-[var(--text-muted)]">applied on swap</div>
        </div>
      </div>
      <div className="text-[10.5px] text-[var(--text-subtle)] uppercase tracking-wider mb-2">
        Route path ({steps.length} step{steps.length > 1 ? "s" : ""})
      </div>
      <div className="space-y-2">
        {steps.map((s, i) => {
          const a = s.action || {};
          const tool = s.tool || s.toolDetails?.name || q.tool || "Direct";
          return (
            <div key={i} className="panel-3 p-3 flex items-center gap-3">
              <div className="w-7 h-7 rounded-md bg-[var(--accent-soft)] border border-[var(--border-accent)] text-[var(--accent)] flex items-center justify-center text-[11px] font-bold font-mono flex-shrink-0">{i + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-[12px] text-[var(--text)]">
                  <span className="font-mono truncate">{a.fromToken?.symbol || fromToken?.symbol}</span>
                  <span className="text-[var(--text-subtle)]">→</span>
                  <span className="font-mono truncate">{a.toToken?.symbol || toToken?.symbol}</span>
                </div>
                <div className="text-[11px] text-[var(--text-muted)] mt-0.5 truncate">
                  via <span className="text-[var(--text)] capitalize">{tool}</span>
                  {a.fromChainId && a.toChainId && a.fromChainId !== a.toChainId ? " · cross-chain" : " · same-chain"}
                </div>
              </div>
              {s.estimate?.executionDuration ? <span className="pill">{s.estimate.executionDuration}s</span> : null}
            </div>
          );
        })}
      </div>
      <div className="mt-4 text-[11px] text-[var(--text-subtle)] font-mono flex items-center justify-between">
        <span className="truncate">{fromChain?.name} → {toChain?.name}</span>
        <span>{ctx?.mode || "quote"}</span>
      </div>
    </div>
  );
}
